import { bomSchema, createBomSchema, formBomSchema, type Bom, } from "@/features/dashboard/types/bom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "../core/forms/form";
import { Textarea } from "../core/forms/textarea";
import { Input } from "../core/forms/input";
import Select from "../core/forms/select";
import { useEffect } from "react";
import { Button } from "../core/buttons/button";
import { useBomBrandOriginOptions } from "@/features/dashboard/api/queries/bom-brand-origin-query";
import { useMarkupRates, usePeriodTypes, useUnitTypes } from "@/hooks/use-enums";
import MultiCreatableSelect from "../core/forms/multi-creatable-select";
import { useBomBrandOptions } from "@/features/dashboard/api/queries/bom-brand-query";
import { useBomSupplierInfoOptions } from "@/features/dashboard/api/queries/bom-supplier-info-query";
import { useBomItemsOptions } from "@/features/dashboard/api/queries/bom-item-query";
import CreatableSelect from "../core/forms/creatable-select";
import { CreateBomModal } from "./create-bom-modal";

interface Props {
    bom?: Bom;
    onSubmit: (values: Bom) => Promise<void>;
    isSubmitting?: boolean;
}

export function BomForm({ bom, onSubmit, isSubmitting }: Props) {
    const { data: unitTypeOptions = [] } = useUnitTypes();
    const { data: periodTypeOptions = [] } = usePeriodTypes();
    const { data: markupRateOptions = [] } = useMarkupRates();
    const { options: brandOriginOptions } = useBomBrandOriginOptions();
    const { options: brandOptions } = useBomBrandOptions();
    const { options: supplierOptions } = useBomSupplierInfoOptions();
    const { options: itemOptions } = useBomItemsOptions();

    const form = useForm<Bom>({
        resolver: zodResolver(formBomSchema),
        mode: "onTouched",
        defaultValues: bom
            ? {
                ...bom,
                specifications: Array.isArray(bom.specifications) || !bom.specifications
                    ? {}
                    : bom.specifications,
                additional_costs: Array.isArray(bom.additional_costs) || !bom.additional_costs
                    ? {}
                    : bom.additional_costs,
            }
            : {
                bom_supplier_info_id: "",
                item_name: "",
                brand_model: [],
                brand_origin: [],
                specifications: {},
                additional_costs: {},
                markup_rate: 0,
                warranty: 0,
                period: "",
                quantity: 0,
                unit: "each",
                company_unit_cost: 0,
                company_total_cost: 0,
                supplier_unit_cost: 0,
                supplier_total_cost: 0,
            },
    });

    // form.watch = using it to create a reactive link between your inputs:
    const quantity = form.watch("quantity");
    const supplier_unit_cost = form.watch("supplier_unit_cost");
    const additional_costs = form.watch("additional_costs");
    const markup_rate = form.watch("markup_rate");

    useEffect(() => {
        const rate = parseFloat(markup_rate) || 1.0;
        const productQuantity = Number(quantity) || 0;
        const baseSupplierUnitCost = Number(supplier_unit_cost) || 0;
        const additionalTotal = Object.values(additional_costs || {}).reduce((sum, v) => sum + (Number(v) || 0), 0);
        const effectiveSupplierUnitCost = baseSupplierUnitCost + additionalTotal;
        const supplierTotal = productQuantity * effectiveSupplierUnitCost;
        const rawCompanyUnitCost = baseSupplierUnitCost * rate;
        const companyUnitCost = Math.ceil(rawCompanyUnitCost / 100) * 100;
        const companyTotal = productQuantity * companyUnitCost;
        form.setValue("supplier_total_cost", supplierTotal);
        form.setValue("company_unit_cost", companyUnitCost);
        form.setValue("company_total_cost", companyTotal)
        console.log('company total cost', companyTotal);
        console.log('rate', rate);

    }, [quantity, supplier_unit_cost, additional_costs, markup_rate]);

    const handleFormSubmit = async (values: Bom) => {
        try {
            await onSubmit(values);
        } catch (error: any) {
            const fieldErrors = error?.response?.data?.errors as Record<string, string[]> | undefined;
            if (error?.response?.status === 422 && fieldErrors) {
                Object.entries(fieldErrors).forEach(([field, messages]) => {
                    form.setError(field as keyof Bom, {
                        type: "server",
                        message: messages[0] ?? "Invalid value",
                    });
                });
            } else {
                throw error;
            }
        }
    };
    return (
        <Form {...form}>
            <form id="bom-form" className="flex flex-col w-full gap-6" onSubmit={form.handleSubmit(handleFormSubmit)}>
                <div className="flex flex-col w-full gap-6">
                    <div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
                        <div className="col-span-12">
                            <FormField
                                control={form.control}
                                name="bom_supplier_info_id"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>Supplier Name</FormLabel>
                                        <FormGroup>
                                            <Select
                                                placeholder="Select supplier"
                                                options={supplierOptions}
                                                defaultValue={field.value ?? undefined}
                                                onValueChange={(value) => field.onChange(value || "")}
                                            />
                                        </FormGroup>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
                        <div className="col-span-6">
                            <FormField
                                control={form.control}
                                name="item_name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>Item Name</FormLabel>
                                        <FormGroup>
                                            <CreatableSelect
                                                placeholder="Select or type to add a new item"
                                                options={itemOptions}
                                                defaultValue={field.value ?? undefined}
                                                onValueChange={(value) => field.onChange(value ?? "")}
                                                isValidNewOption={(input) => input.trim().length >= 2}
                                                formatCreateLabel={(input) => `Add "${input}"`}
                                            />
                                        </FormGroup>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className="col-span-6">
                            <FormField
                                control={form.control}
                                name="brand_model"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>Brand Name</FormLabel>
                                        <FormGroup>
                                            <MultiCreatableSelect
                                                placeholder="Select or type to add brand name"
                                                options={brandOptions}
                                                value={field.value ?? []}
                                                onValueChange={(values) => field.onChange(values)}
                                                isValidNewOption={(input) => input.trim().length >= 2}
                                                formatCreateLabel={(input) => `Add "${input}"`}
                                            />
                                        </FormGroup>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className="col-span-6 col-start-1">
                            <FormField
                                control={form.control}
                                name="brand_origin"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Brand Origin</FormLabel>
                                        <FormGroup>
                                            <MultiCreatableSelect
                                                placeholder="Select or type to add brand origin"
                                                options={brandOriginOptions}
                                                value={field.value ?? []}
                                                onValueChange={(values) => field.onChange(values)}
                                                isValidNewOption={(input) => input.trim().length >= 2}
                                                formatCreateLabel={(input) => `Add "${input}"`}
                                            />
                                        </FormGroup>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
                        <div className="col-span-12">
                            <FormField
                                control={form.control}
                                name="specifications"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Specifications</FormLabel>
                                        <FormGroup>
                                            <div className="flex flex-col gap-2">
                                                {Object.entries(
                                                    field.value || {}).map(([key, value], index) => (
                                                        <div key={index} className="flex gap-2">
                                                            <Input
                                                                placeholder="(E.g., Processor)"
                                                                value={key}
                                                                onChange={(e) => {
                                                                    const newEntries = Object.entries(field.value || {}).map(
                                                                        ([k, v]) => k === key ? [e.target.value, v] : [k, v]
                                                                    );
                                                                    field.onChange(Object.fromEntries(newEntries))
                                                                }}
                                                            />
                                                            <Input
                                                                placeholder="Value (E.g., Intel Core i5)"
                                                                value={value as string}
                                                                onChange={(e) => {
                                                                    field.onChange({
                                                                        ...field.value,
                                                                        [key]: e.target.value
                                                                    })
                                                                }}
                                                            />
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                onClick={() => {
                                                                    const newSpecs = { ...field.value }
                                                                    delete newSpecs[key]
                                                                    field.onChange(newSpecs)
                                                                }}
                                                            >
                                                                Remove
                                                            </Button>
                                                        </div>
                                                    ))}
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    onClick={() => field.onChange({ ...field.value, "": "" })}
                                                >
                                                    + Add Specification
                                                </Button>
                                            </div>
                                        </FormGroup>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
                        <div className="col-span-6">
                            <FormField
                                control={form.control}
                                name="warranty"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Warranty</FormLabel>
                                        <FormGroup>
                                            <Input
                                                className="input"
                                                type="number"
                                                min="0"
                                                placeholder="Enter warranty"
                                                {...field}
                                                value={field.value || ""}
                                                onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : 0)}
                                            />
                                        </FormGroup>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className="col-span-6">
                            <FormField
                                control={form.control}
                                name="period"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Period</FormLabel>
                                        <FormGroup>
                                            <Select
                                                placeholder="Select unit"
                                                options={periodTypeOptions}
                                                defaultValue={field.value}
                                                onValueChange={(value) => field.onChange(value || "")}
                                            />
                                        </FormGroup>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className="col-span-6">
                            <FormField
                                control={form.control}
                                name="quantity"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>Quantity</FormLabel>
                                        <FormGroup>
                                            <Input
                                                className="input"
                                                type="number"
                                                placeholder="Enter quantity"
                                                min="0"
                                                {...field}
                                                value={field.value || ""}
                                                onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : 0)}
                                            />
                                        </FormGroup>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
                        <div className="col-span-6">
                            <FormField
                                control={form.control}
                                name="unit"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Unit</FormLabel>
                                        <FormGroup>
                                            <Select
                                                placeholder="Select unit"
                                                options={unitTypeOptions}
                                                defaultValue={field.value}
                                                onValueChange={(value) => field.onChange(value || "each")}
                                            />
                                        </FormGroup>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
                        <div className="col-span-12">
                            <FormField
                                control={form.control}
                                name="additional_costs"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Additional Costs</FormLabel>
                                        <FormGroup>
                                            <div className="flex flex-col gap-2">
                                                {Object.entries(field.value || {}).map(([key, value], index) => (
                                                    <div key={index} className="flex gap-2">
                                                        <Input
                                                            placeholder="Cost label (e.g., Shipping)"
                                                            value={key}
                                                            onChange={(e) => {
                                                                const newEntries = Object.entries(field.value || {}).map(
                                                                    ([k, v]) => k === key ? [e.target.value, v] : [k, v]
                                                                );
                                                                field.onChange(Object.fromEntries(newEntries));
                                                            }}
                                                        />
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            placeholder="0.00"
                                                            value={value as number}
                                                            onChange={(e) => {
                                                                field.onChange({
                                                                    ...field.value,
                                                                    [key]: e.target.value ? Number(e.target.value) : 0,
                                                                });
                                                            }}
                                                        />
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            onClick={() => {
                                                                const next = { ...field.value };
                                                                delete next[key];
                                                                field.onChange(next);
                                                            }}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </div>
                                                ))}
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    onClick={() => field.onChange({ ...field.value, "": 0 })}
                                                >
                                                    + Add Cost
                                                </Button>
                                            </div>
                                        </FormGroup>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
                        <div className="col-span-6">
                            <FormField
                                control={form.control}
                                name="markup_rate"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Markup Rate</FormLabel>
                                        <FormGroup>
                                            <CreatableSelect
                                                placeholder="Select or type a markup rate (e.g., 1.5)"
                                                options={markupRateOptions}
                                                defaultValue={field.value ?? undefined}
                                                // if user choses the preset
                                                onValueChange={(value) => {
                                                    field.onChange(value ?? "");
                                                    form.clearErrors("markup_rate");
                                                }}

                                                onInputChange={(input) => {
                                                    if (!input) {
                                                        form.clearErrors("markup_rate");
                                                        return;
                                                    }
                                                    const n = parseFloat(input);
                                                    if (!Number.isFinite(n) || n <= 0) {
                                                        form.setError("markup_rate", {
                                                            type: "manual",
                                                            message: "Markup rate must be a positive number (e.g., 1.5)",
                                                        });

                                                    } else {
                                                        form.clearErrors("markup_rate");
                                                    }
                                                }}

                                                isValidNewOption={(input) => {
                                                    const n = parseFloat(input);
                                                    return Number.isFinite(n) && n > 0;
                                                }}
                                                formatCreateLabel={(input) => `Use "${input}"`}


                                            />
                                        </FormGroup>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
                        <div className="col-span-6">
                            <FormField
                                control={form.control}
                                name="supplier_unit_cost"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>Supplier unit cost</FormLabel>
                                        <FormGroup>
                                            <Input
                                                className="input"
                                                type="number"
                                                step="0.01"
                                                placeholder="0.00"
                                                name={field.name}
                                                value={field.value || ""}
                                                onBlur={field.onBlur}
                                                min="0"
                                                onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : 0)}
                                            />
                                        </FormGroup>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className="col-span-6">
                            <FormField
                                control={form.control}
                                name="supplier_total_cost"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Supplier total cost <span className="text-gray-500"> (with additional costs)</span></FormLabel>
                                        <FormGroup>
                                            <Input
                                                className="input bg-gray-300"
                                                type="number"
                                                step="0.01"
                                                placeholder="0.00"
                                                min="0"
                                                readOnly
                                                {...field}
                                                value={field.value || ""}
                                            />
                                        </FormGroup>
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
                        <div className="col-span-6">
                            <FormField
                                control={form.control}
                                name="company_unit_cost"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Company unit cost</FormLabel>
                                        <FormGroup>
                                            <Input
                                                className="input bg-gray-300"
                                                type="number"
                                                step="0.01"
                                                placeholder="0.00"
                                                name={field.name}
                                                value={field.value || ""}
                                                onBlur={field.onBlur}
                                                min="0"
                                                readOnly
                                            // onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : 0)}
                                            />
                                        </FormGroup>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className="col-span-6">
                            <FormField
                                control={form.control}
                                name="company_total_cost"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Company total cost</FormLabel>
                                        <FormGroup>
                                            <Input
                                                className="input bg-gray-300"
                                                type="number"
                                                step="0.01"
                                                placeholder="0.00"
                                                min="0"
                                                readOnly
                                                {...field}
                                                value={field.value || ""}
                                            />
                                        </FormGroup>
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>
                </div>
            </form>
        </Form>
    )
}